// ── Event allowlist ──────────────────────────────────────────────────────────
// The runtime half of the contract in types.ts. Types vanish at build time, so the same list has to
// exist as data for anything to be checked while the app runs.
//
// The two halves cannot drift: `required` and `optional` are typed from the event union itself
// (RequiredParamsOf / OptionalParamsOf), so naming a parameter an event does not accept is a
// compile error here. What the compiler cannot check is completeness — a parameter the type allows
// but this table forgets is simply stripped at runtime, which shows up as a missing column in the
// report rather than as leaked data. That is the safe direction to fail in.

import {
  type EventContext,
  type EventName,
  type EventOf,
  type OptionalParamsOf,
  type RequiredParamsOf,
  type TrackingEvent,
} from "./types";

/** Dev and test builds check the whole contract; read per call so a test can flip it. */
const strict = () => import.meta.env.DEV;

// ── Envelope keys ────────────────────────────────────────────────────────────

// A Record<keyof EventContext, true> is what forces this list to stay complete: add a field to the
// envelope without adding it here and the object no longer satisfies the type. The keys are the
// allowlist — envelope fields are never stripped, whatever event carries them. Every one is required
// except those in OPTIONAL_CONTEXT_FIELDS.
const CONTEXT_FIELDS: Record<keyof EventContext | "event", true> = {
  event: true,
  event_id: true,
  tracking_schema_version: true,
  event_timestamp: true,
  environment: true,
  app_version: true,
  tenant_id: true,
  brand_mode: true,
  user_group: true,
  auth_state: true,
  business_flow: true,
  event_source: true,
  consent_policy_version: true,
  locked_dealer_id: true,
};

/** Envelope fields that are left out when there is nothing to say, rather than sent empty. */
const OPTIONAL_CONTEXT_FIELDS: ReadonlySet<string> = new Set<keyof EventContext>(["locked_dealer_id"]);

/** Envelope keys, always allowed. */
export const CONTEXT_KEYS: readonly string[] = Object.keys(CONTEXT_FIELDS);

/** Envelope keys every event must carry. */
const REQUIRED_CONTEXT_KEYS: readonly string[] = CONTEXT_KEYS.filter((key) => !OPTIONAL_CONTEXT_FIELDS.has(key));

// ── Per-event parameters ─────────────────────────────────────────────────────

export interface EventSchema<E extends EventName> {
  readonly required: readonly RequiredParamsOf<EventOf<E>>[];
  readonly optional: readonly OptionalParamsOf<EventOf<E>>[];
}

/**
 * The allowlist. Every event the app may raise, and every parameter it may carry.
 * <p>The mapped type over EventName means an event added to the union in types.ts but forgotten
 * here fails to compile — there is no path where an event exists in one half only.</p>
 */
export const EVENT_SCHEMA: { readonly [E in EventName]: EventSchema<E> } = {
  // Page and catalogue
  page_view: { required: ["page_location", "page_title"], optional: ["page_referrer"] },
  view_item_list: { required: ["item_list_id", "item_list_name", "items"], optional: [] },
  select_item: { required: ["items"], optional: ["item_list_id", "item_list_name"] },
  view_item: { required: ["items"], optional: ["door_model_id", "value", "currency"] },

  // Configurator
  configurator_start: {
    required: ["configuration_id"],
    optional: ["configuration_version", "door_model_id"],
  },
  configurator_step_view: {
    required: ["configuration_id", "configurator_step"],
    optional: ["configurator_step_index"],
  },
  configurator_step_complete: {
    required: ["configuration_id", "configurator_step"],
    optional: ["configurator_step_index"],
  },
  configurator_option_select: {
    required: ["configuration_id", "configurator_step", "option_group", "option_id"],
    optional: ["door_model_id"],
  },
  configurator_validation_error: {
    required: ["configuration_id", "configurator_step", "error_code"],
    optional: ["option_group"],
  },
  configurator_save: {
    required: ["configuration_id"],
    optional: ["configuration_version", "door_model_id", "product_kinds", "item_count"],
  },
  configurator_resume: { required: ["configuration_id"], optional: ["configuration_version"] },
  configurator_complete: {
    required: ["configuration_id"],
    optional: ["configuration_version", "door_model_id", "value", "currency", "items", "product_kinds", "item_count"],
  },

  // Account
  sign_up: { required: ["method"], optional: [] },
  login: { required: ["method"], optional: [] },
  logout: { required: [], optional: [] },

  // Dealer selection and lead
  dealer_list_view: { required: ["dealer_count"], optional: ["configuration_id"] },
  dealer_select: { required: ["dealer_id"], optional: ["configuration_id"] },
  quote_request_start: {
    required: ["configuration_id"],
    optional: ["dealer_count", "dealer_id", "product_kinds", "item_count"],
  },
  // No value, no currency — a quotation request is not revenue. The type says the same thing.
  generate_lead: {
    required: ["lead_id", "dealer_count"],
    optional: ["configuration_id", "dealer_id", "lead_source", "product_kinds", "item_count"],
  },

  // Lead lifecycle. Raised server-side by the status transitions themselves, never by the browser:
  // there is no CRM, and the dealer moves a lead inside this application.
  qualify_lead: { required: ["lead_id"], optional: ["dealer_id", "lead_source"] },
  working_lead: { required: ["lead_id"], optional: ["dealer_id", "lead_source"] },
  disqualify_lead: { required: ["lead_id"], optional: ["dealer_id", "reason_code"] },
  close_convert_lead: {
    required: ["lead_id"],
    optional: ["dealer_id", "value", "currency", "transaction_id"],
  },
  close_unconvert_lead: { required: ["lead_id"], optional: ["dealer_id", "reason_code"] },

  // Checkout and order
  begin_checkout: {
    required: ["handoff_id", "configuration_id"],
    optional: ["value", "currency", "items", "product_kinds", "item_count"],
  },
  checkout_handoff_error: { required: ["error_code"], optional: ["configuration_id", "handoff_id"] },
  purchase: {
    required: ["transaction_id"],
    optional: ["value", "currency", "items", "lead_id", "configuration_id"],
  },
  refund: { required: ["transaction_id"], optional: ["value", "currency", "items"] },

  // Dashboard
  dashboard_view: { required: [], optional: ["project_count", "filter_key"] },
  dashboard_search: { required: ["search_length"], optional: [] },
  dashboard_filter: { required: ["filter_key"], optional: ["project_count"] },
  dashboard_sort: { required: ["sort_field", "sort_direction"], optional: [] },
  dashboard_project_open: { required: ["configuration_id"], optional: [] },
  dashboard_project_create: { required: [], optional: ["configuration_id"] },
  dashboard_project_duplicate: { required: ["configuration_id"], optional: [] },
  dashboard_project_rename: { required: ["configuration_id"], optional: [] },
  dashboard_project_archive: { required: ["configuration_id"], optional: [] },
  dashboard_project_favorite: { required: ["configuration_id"], optional: [] },
  dashboard_project_delete: { required: ["configuration_id"], optional: [] },
};

const KNOWN_EVENTS = new Set<string>(Object.keys(EVENT_SCHEMA));

/** Every event name in the allowlist, in declaration order. */
export const EVENT_NAMES = Object.keys(EVENT_SCHEMA) as readonly EventName[];

export function isKnownEvent(name: string): name is EventName {
  return KNOWN_EVENTS.has(name);
}

// ── Validation ───────────────────────────────────────────────────────────────

/**
 * The gate every event passes through on its way to the data layer.
 *
 * <p>Two rules run in every build, production included, because they are privacy controls rather
 * than diagnostics: an event that is not in the allowlist is refused, and a parameter the event does
 * not declare is stripped. An undeclared parameter is precisely how a customer name reaches GA4 —
 * someone adds a field at a call site, no one reviews it, and it ships. Here it never leaves.</p>
 *
 * <p>The rest — the complaints on the console, and rejecting an event that is missing a required
 * field — runs only in dev and test. In production a generate_lead without its lead_id is still
 * worth more than no generate_lead at all, so it ships incomplete rather than being dropped.</p>
 *
 * @returns the cleaned event, or null if it must not be sent.
 */
export function validateEvent(candidate: unknown): TrackingEvent | null {
  if (typeof candidate !== "object" || candidate === null || Array.isArray(candidate)) {
    if (strict()) console.error("[tracking] Not an event object, rejected:", candidate);
    return null;
  }

  const source = candidate as Record<string, unknown>;
  const name = source.event;

  if (typeof name !== "string" || !isKnownEvent(name)) {
    if (strict()) {
      console.error(
        `[tracking] Unknown event "${String(name)}", rejected. Add it to types.ts and EVENT_SCHEMA first.`,
      );
    }
    return null;
  }

  // The union of EventSchema<E> collapses to plain string arrays here; the compile-time narrowing
  // has already done its work in the table above.
  const schema: { required: readonly string[]; optional: readonly string[] } = EVENT_SCHEMA[name];
  const allowed = new Set<string>([...CONTEXT_KEYS, ...schema.required, ...schema.optional]);

  const cleaned: Record<string, unknown> = {};
  const dropped: string[] = [];

  for (const [key, value] of Object.entries(source)) {
    // An absent parameter and a null one mean the same thing to us, but GA4 keeps "null" as a value.
    if (value === undefined || value === null) continue;
    if (allowed.has(key)) cleaned[key] = value;
    else dropped.push(key);
  }

  if (dropped.length > 0 && strict()) {
    console.error(
      `[tracking] "${name}" carried parameters it does not declare: ${dropped.join(", ")}. Stripped.`,
    );
  }

  const missing = [
    ...REQUIRED_CONTEXT_KEYS.filter((key) => !(key in cleaned)),
    ...schema.required.filter((param) => !(param in cleaned)),
  ];

  if (missing.length > 0 && strict()) {
    console.error(`[tracking] "${name}" is missing required fields: ${missing.join(", ")}. Rejected.`);
    return null;
  }

  return cleaned as unknown as TrackingEvent;
}
